import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { theme, Alert } from 'antd';
import { embedDashboard } from "@superset-ui/embedded-sdk";
import { ThemeContext } from '../../index';

/** Matches Superset `/api/v1/security/login` `provider` for the deployment's AUTH_TYPE (db vs ldap). */
function resolveSupersetLoginProvider(authProviderProp) {
  const raw = (authProviderProp ?? process.env.REACT_APP_SUPERSET_AUTH_PROVIDER ?? 'db')
    .toString()
    .trim()
    .toLowerCase();
  return raw === 'ldap' ? 'ldap' : 'db';
}

const SupersetDashboard = ({
  baseURL,
  dashboardId,
  username,
  password,
  first_name,
  last_name,
  selectedDashboardId,
  authProvider,
}) => {
  const [accessToken, setAccessToken] = useState(null);
  const [guestToken, setGuestToken] = useState(null);
  const [error, setError] = useState(null);
  const { themeConfig } = useContext(ThemeContext);

  const { algorithm } = themeConfig;
  // Check if app is in dark mode
  const isDark = algorithm === theme.darkAlgorithm;

  // Create axios instance using the baseURL prop
  const api = axios.create({
    baseURL,  // Using the prop `baseURL`
    withCredentials: true,  // Include cookies in requests
  });

  // Function to log in and get the access token
  const login = async () => {
    try {
      const response = await api.post('/api/v1/security/login', {
        username,
        password,
        provider: resolveSupersetLoginProvider(authProvider),
        refresh: true,
      });
      return response.data.access_token;
    } catch (error) {
      console.error('Error during login:', error);
      setError('Unable to connect to the Superset API. Please contact the administrator.');
      return null;
    }
  };

  const fetchGuestToken = async (token, targetDashboardId) => {
    try {
      // Ensure dashboard ID is a string (Superset expects string format)
      const dashId = String(targetDashboardId);
            
      const response = await api.post('/api/v1/security/guest_token/', {
        resources: [
          {
            id: dashId,
            type: "dashboard"
          }
        ],
        rls: [],
        user: {
          first_name: first_name,
          last_name: last_name,
          username: username
        }
      }, { headers: { 'Authorization': `Bearer ${token}` } });
      return response.data.token;
    } catch (error) {
      console.error('Error fetching guest token:', error);
      if (error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
        console.error('Error headers:', error.response.headers);
      }
      setError('Unable to fetch guest token from Superset. Please contact the administrator.');
      return null;
    }
  };

  // Initialize session and get the access token
  useEffect(() => {
    // Validate required props first
    if (!baseURL) {
      setError('Superset base URL is not configured');
      return;
    }
    if (!dashboardId) {
      setError('Dashboard ID is not configured');
      return;
    }
    if (!username || !password) {
      setError('Superset credentials are not configured');
      return;
    }

    // Clear any previous errors and initialize
    setError(null);
    setAccessToken(null);
    setGuestToken(null);

    const initializeSession = async () => {
      const token = await login();
      if (token) {
        setAccessToken(token);
      }
    };

    initializeSession();
  }, [baseURL, dashboardId, username, password, authProvider]);

  useEffect(() => {
    const getGuestToken = async () => {
      const effectiveDashboardId = selectedDashboardId || dashboardId;
      if (accessToken && effectiveDashboardId) {
        const token = await fetchGuestToken(accessToken, effectiveDashboardId);
        if (token) {
          setGuestToken(token);
        }
      }
    };

    getGuestToken();
  }, [accessToken, selectedDashboardId, dashboardId]);

  useEffect(() => {
    const embedDashboardInstance = async () => {
      const effectiveDashboardId = selectedDashboardId || dashboardId;
      if (!guestToken || !effectiveDashboardId) return;

      const container = document.getElementById('dashboard-container');
      if (container) {
        container.innerHTML = '';
      }

      try {
        await embedDashboard({
          id: effectiveDashboardId,
          supersetDomain: baseURL,
          mountPoint: container,
          fetchGuestToken: () => guestToken,
          dashboardUiConfig: {
            filters: {
              expanded: false,
              visible: false
            },
            hideTitle: true,
            hideChartControls: true,
            hideTab: true,
          },
          // Light by default: no template. Dark only when app is dark.
          cssTemplates: isDark ? ['dark_template'] : undefined,
        });
      } catch (error) {
        console.error('Error embedding dashboard:', error);
        setError('Unable to connect to the Stat API. Please contact the administrator.');
      }
    };

    embedDashboardInstance();
  }, [guestToken, selectedDashboardId, baseURL, algorithm]);

  // If there's an error, display the error message
  if (error) {
    return (
      <Alert
      message="Feature Unavailable"
      description={error}
      type="warning"
      showIcon
      closable
      onClose={() => setError(null)}
    />

    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        margin: 0,
        padding: 0,
      }}
    >
      <div
        id="dashboard-container"
        className={`superset-dashboard ${!isDark ? 'dark-mode' : ''}`}
        style={{
          width: '100%',
          flex: 1,
          minHeight: 480,
          height: 'min(70vh, 900px)',
          maxWidth: '100%',
          position: 'relative',
          overflow: 'hidden',
          margin: 0,
          padding: 0,
        }}
      />
    </div>
  );
};

export default SupersetDashboard;
